import React, { useEffect, useState } from 'react';
import { StepType, StepIntent, AllowedStepIntentOption } from '../../types';
import apiService from '../../services/api';
import { stepTypeConfig } from '../../utils/stepTypeConfig';

interface IntentAwareStepSelectorProps {
  onSelect: (stepType: StepType, intent?: StepIntent) => void;
  onClose: () => void;
  prevStepType?: StepType;
  prevIntent?: StepIntent;
  orchestrationId?: number;
}

interface GroupedStepOptions {
  stepType: StepType;
  options: AllowedStepIntentOption[];
}

const IntentAwareStepSelector: React.FC<IntentAwareStepSelectorProps> = ({
  onSelect,
  onClose,
  prevStepType,
  prevIntent,
  orchestrationId,
}) => {
  const [allowedOptions, setAllowedOptions] = useState<AllowedStepIntentOption[]>([]);
  const [groupedOptions, setGroupedOptions] = useState<GroupedStepOptions[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.debug('[IntentAwareStepSelector] Fetching allowed intents for:', { prevStepType, prevIntent });

    const fetchAllowedIntents = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const options = await apiService.getAllowedIntents({
          prev_step_type: prevStepType,
          prev_intent: prevIntent,
          orchestration_id: orchestrationId,
        });

        console.debug('[IntentAwareStepSelector] Received allowed options:', options);

        setAllowedOptions(options);

        // Group options by step type
        const grouped = groupOptionsByStepType(options);
        setGroupedOptions(grouped);

      } catch (error) {
        console.error('Error fetching allowed intents:', error);
        setError('Failed to load available step options. Please try again.');

        // Fallback to basic step types without intent filtering
        const fallbackOptions: AllowedStepIntentOption[] = Object.keys(stepTypeConfig).map(stepType => ({
          step_type: stepType as StepType,
          intent: null,
        }));
        setAllowedOptions(fallbackOptions);
        setGroupedOptions(groupOptionsByStepType(fallbackOptions));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllowedIntents();
  }, [prevStepType, prevIntent, orchestrationId]);

  const groupOptionsByStepType = (options: AllowedStepIntentOption[]): GroupedStepOptions[] => {
    const grouped = new Map<StepType, AllowedStepIntentOption[]>();

    options.forEach(option => {
      if (!grouped.has(option.step_type)) {
        grouped.set(option.step_type, []);
      }
      grouped.get(option.step_type)!.push(option);
    });

    return Array.from(grouped.entries()).map(([stepType, stepOptions]) => ({
      stepType,
      options: stepOptions,
    }));
  };

  const handleOptionSelect = (option: AllowedStepIntentOption) => {
    onSelect(option.step_type, option.intent || undefined);
  };

  const getIntentDisplayName = (intent: StepIntent | null): string => {
    if (!intent) return '';

    const intentNames = {
      create: 'Create',
      enhance: 'Enhance',
      validate: 'Validate',
      conditional: 'Conditional',
      assistant: 'Assistant',
    };

    return intentNames[intent] || intent;
  };

  const getIntentDescription = (intent: StepIntent | null): string => {
    if (!intent) return '';

    const descriptions = {
      create: 'Generate new content or output',
      enhance: 'Improve or extend existing content',
      validate: 'Check content against rules or criteria',
      conditional: 'Make decisions based on conditions',
      assistant: 'Interactive conversation with context',
    };

    return descriptions[intent] || '';
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-cap-dark rounded-lg p-4 sm:p-6 w-full max-w-[700px] max-h-[80vh] overflow-y-auto mx-4 sm:mx-0">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">Select Step Type</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading available options...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-cap-dark rounded-lg p-4 sm:p-6 w-full max-w-[700px] max-h-[80vh] overflow-y-auto mx-4 sm:mx-0">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">Select Step Type</h2>
            {prevStepType && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Following: {stepTypeConfig[prevStepType]?.name || prevStepType}
                {prevIntent && ` (${getIntentDisplayName(prevIntent)})`}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Error Loading Options</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-400">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {groupedOptions.map(({ stepType, options }) => {
            const config = stepTypeConfig[stepType];
            if (!config) return null;

            return (
              <div key={stepType} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-tutorial={`step-type-${stepType}`}>
                <div className="flex items-center mb-3">
                  <config.icon className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">{config.name}</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{config.description}</p>

                {/* Show intent options for RUN_AGENT, or single option for other types */}
                <div className="grid grid-cols-1 gap-2">
                  {options.map((option, index) => (
                    <button
                      key={`${option.step_type}-${option.intent || 'default'}-${index}`}
                      onClick={() => handleOptionSelect(option)}
                      className={`flex flex-col items-start p-3 rounded-lg border text-left transition-colors duration-200 ${config.color} ${config.borderColor} ${config.hoverColor}`}
                      data-tutorial={`step-option-${option.step_type}${option.intent ? `-${option.intent}` : ''}`}
                    >
                      <div className="flex items-center justify-between w-full mb-1">
                        <span className="font-medium">
                          {stepType === 'run_agent' && option.intent
                            ? `${config.name} - ${getIntentDisplayName(option.intent)}`
                            : config.name
                          }
                        </span>
                        {stepType === 'run_agent' && option.intent && (
                          <span className="text-xs px-2 py-1 bg-white dark:bg-cap-dark bg-opacity-50 rounded">
                            {option.intent}
                          </span>
                        )}
                      </div>
                      {stepType === 'run_agent' && option.intent && (
                        <p className="text-sm opacity-90">
                          {getIntentDescription(option.intent)}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {allowedOptions.length === 0 && !isLoading && (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-warm-white">No Available Options</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No valid step types are available after the selected previous step.
            </p>
          </div>
        )}

        {/* Intent validation info */}
        <div className="mt-6 p-4 bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                Intent-Based Workflow Validation
              </h3>
              <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
                <p>
                  Only compatible step types and intents are shown based on your workflow's logic.
                  This ensures your workflow follows valid patterns and produces reliable results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntentAwareStepSelector;